import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CaretLeft, Check, Clock, ClipboardText, HandCoins, CheckCircle, CurrencyDollar, Lock, Wrench, Flag, Star, Wallet } from '@phosphor-icons/react';

const ICON_MAP = {
  posted: ClipboardText, bid_received: HandCoins, bid_accepted: CheckCircle,
  booking_paid: CurrencyDollar, escrow_paid: Lock, artisan_confirmed: CheckCircle,
  work_started: Wrench, milestone_completed: Flag, milestone_approved: Check,
  work_completed: Star, payment_released: Wallet, rated: Star,
};

export default function ProgressTrackerPage() {
  const { jobId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [timeline, setTimeline] = useState([]);
  const [jobStatus, setJobStatus] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}/timeline`);
        setTimeline(data.timeline || []);
        setJobStatus(data.job_status || '');
      } catch {} finally { setLoading(false); }
    };
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [jobId, api]);

  if (loading) return <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] flex items-center justify-center"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>;

  const doneCount = timeline.filter(e => e.status === 'done').length;
  const progress = timeline.length > 0 ? Math.round((doneCount / timeline.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-10 max-w-[480px] mx-auto" data-testid="progress-tracker-page">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(`/job/${jobId}`)} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
        <h1 className="font-heading text-lg font-bold text-[#1C1C1C] flex-1">Job Progress</h1>
        <span className={`text-xs font-bold px-2 py-1 rounded-full ${jobStatus === 'completed' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#E86A33]/10 text-[#E86A33]'}`}>{jobStatus?.replace(/_/g, ' ')}</span>
      </header>

      <div className="p-4">
        {/* Progress Ring */}
        <div className="bg-white border border-[#D4C5B3] rounded-2xl p-6 flex items-center gap-6 mb-4">
          <div className="relative w-20 h-20 flex-shrink-0">
            <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="35" fill="none" stroke="#E5E3DB" strokeWidth="6" />
              <circle cx="40" cy="40" r="35" fill="none" stroke={progress >= 100 ? '#2D6A4F' : '#E86A33'} strokeWidth="6" strokeDasharray={`${progress * 2.2} 220`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-heading text-lg font-black text-[#1C1C1C]">{progress}%</div>
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-[#1C1C1C]">
              {jobStatus === 'completed' ? 'Job Complete!' : jobStatus === 'disputed' ? 'Under Dispute' : 'In Progress'}
            </h2>
            <p className="text-sm text-[#5C5C5C] font-body mt-1">{doneCount} of {timeline.length} steps completed</p>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative" data-testid="timeline">
          {/* Vertical line */}
          <div className="absolute left-5 top-6 bottom-6 w-0.5 bg-[#E5E3DB]" />

          <div className="space-y-0">
            {timeline.map((event, i) => {
              const Icon = ICON_MAP[event.event_type] || Flag;
              const isDone = event.status === 'done';
              const isPending = event.status === 'pending';
              const isLast = i === timeline.length - 1;

              return (
                <div key={i} className={`relative flex items-start gap-4 py-3 ${isPending ? 'opacity-50' : ''}`} data-testid={`timeline-event-${i}`}>
                  {/* Node */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2 ${isDone ? 'bg-[#2D6A4F] border-[#2D6A4F] text-white' : 'bg-white border-[#D4C5B3] text-[#5C5C5C]'}`}>
                    {isDone ? <Check size={16} weight="bold" /> : <Icon size={16} />}
                  </div>
                  {/* Content */}
                  <div className="flex-1 pt-1.5">
                    <p className={`text-sm font-bold font-body ${isDone ? 'text-[#1C1C1C]' : 'text-[#5C5C5C]'}`}>{event.label}</p>
                    {event.note && <p className="text-xs text-[#5C5C5C] font-body mt-0.5">{event.note}</p>}
                    {event.timestamp && (
                      <p className="text-[10px] text-[#5C5C5C] mt-1">
                        {new Date(event.timestamp).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                  {/* Status badge */}
                  {isDone && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F] mt-2">Done</span>}
                  {isPending && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F4A261]/10 text-[#F4A261] mt-2 flex items-center gap-1"><Clock size={10} /> Next</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Back to Job */}
        <button onClick={() => navigate(`/job/${jobId}`)} className="w-full mt-6 bg-[#1A4D2E] text-white font-bold py-3 rounded-xl font-body" data-testid="back-to-job">View Job Details</button>
      </div>
    </div>
  );
}
